import { orderStatusConstants } from "constants/order";
import { parsePrice, centsToDollars } from "helpers/price";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { useState } from "react";
import styled from "styled-components";
import { IProviderAmount } from "types/Payment";
import { SpecialtyList } from "types/Provider/Provider";

import { getProviderReduceByAmount } from "../helpers";

const AddNoteContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 330px;
`;

const PaymentFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const paymentTypeOptions = [
  { label: "Cash", value: "Cash" },
  { label: "Check", value: "Check" },
  { label: "Financing", value: "CareCredit" },
];

export interface IPaymentNoteData {
  isPaymentNote: boolean;
  paymentAmount: number | null;
  paymentType: string | null;
}

export function AddNoteDialog({
  showDialog,
  setShowDialog,
  note,
  setNote,
  paymentData,
  setPaymentData,
  onAddNote,
  orderPaymentNotesEnabled,
  clearPatientFeeValueInPercentage,
  orderStatus,
  providerAmounts,
  transferBeingProcessed,
  installmentAmountInsufficient,
  providerRemainingInsufficient,
  primaryProviderRemaining,
  isGFE,
}: {
  showDialog: boolean;
  setShowDialog: any;
  note: string;
  setNote: any;
  paymentData: IPaymentNoteData;
  setPaymentData: (data: IPaymentNoteData) => void;
  onAddNote: any;
  orderPaymentNotesEnabled?: boolean;
  clearPatientFeeValueInPercentage?: number;
  orderStatus?: string;
  providerAmounts?: IProviderAmount[];
  transferBeingProcessed?: boolean;
  installmentAmountInsufficient?: boolean;
  providerRemainingInsufficient?: boolean;
  primaryProviderRemaining?: number;
  isGFE?: boolean;
}) {
  const [autoReduce, setAutoReduce] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  // The reduction suggestion is hidden once the order is fully paid
  // (still shown for partially paid orders).
  const isPaid = orderStatus === orderStatusConstants.PAID;
  // For partially paid orders we keep the checkbox but do not reduce the
  // patient portion, so the per-provider reduction message is not shown.
  const isPartiallyPaid = orderStatus === orderStatusConstants.PARTIALLY_PAID;

  // The reduction is taken off the first provider with the Primary Provider specialty.
  const primaryProvider = (providerAmounts || []).find(
    (provider) => provider.specialty === SpecialtyList.PRIMARY_PROVIDER
  );
  const primaryProviderName = primaryProvider?.providerName ?? "Provider";
  const primaryProviderPortion = centsToDollars(
    primaryProvider?.patientPortion || 0
  );
  const primaryProviderRemainingDisplay = parsePrice(
    centsToDollars(primaryProviderRemaining ?? 0)
  );

  // Amount the provider should be reduced BY (Cash/Check = full amount,
  // Financing = amount after removing the Clear Patient Fee).
  const reducedProviderAmount = getProviderReduceByAmount(
    paymentData.paymentAmount,
    paymentData.paymentType,
    clearPatientFeeValueInPercentage
  );

  // The reduction block is shown only for GFE orders, when there is a reduction
  // to suggest and the order is not fully paid yet.
  const showReductionBlock =
    !!isGFE &&
    reducedProviderAmount !== null &&
    !!paymentData?.paymentAmount &&
    !isPaid;

  // The primary provider must have enough patient portion to absorb the reduction.
  // Only relevant when we actually reduce the portion (i.e. not partially paid).
  const hasInsufficientPortion =
    showReductionBlock &&
    !isPartiallyPaid &&
    (reducedProviderAmount ?? 0) > primaryProviderPortion;

  // A reduction cannot be applied while a transfer to the providers is in flight.
  const blockedByTransfer = showReductionBlock && !!transferBeingProcessed;

  // For "Set amount" plans, the new remaining must still cover one monthly
  // installment; otherwise the plan needs manual changes before auto-reducing.
  const blockedByInstallment =
    showReductionBlock && !!installmentAmountInsufficient;

  // The reduction cannot exceed the amount remaining to pay to the provider.
  const blockedByRemaining =
    showReductionBlock && !!providerRemainingInsufficient;

  // Auto-reduce is not possible in these cases, but the user can still add a
  // plain note — so we only disable the checkbox, not the Add button.
  const autoReduceBlocked =
    hasInsufficientPortion ||
    blockedByInstallment ||
    blockedByRemaining ||
    blockedByTransfer;

  const resetForm = () => {
    setNote("");
    setAutoReduce(false);
    setPaymentData({
      isPaymentNote: false,
      paymentAmount: null,
      paymentType: null,
    });
  };

  const isAddDisabled = () => {
    if (!note.trim().length) return true;
    if (paymentData.isPaymentNote) {
      return !paymentData.paymentAmount || !paymentData.paymentType;
    }
    return false;
  };

  const footerComponent = () => {
    return (
      <div>
        <Button
          label="Cancel"
          onClick={() => {
            resetForm();
            setShowDialog(false);
          }}
          className="p-button-link mr-2"
        />
        <Button
          label="Add"
          onClick={async () => {
            setAddLoading(true);
            try {
              await onAddNote(
                autoReduce && !autoReduceBlocked,
                reducedProviderAmount
              );
            } finally {
              setAddLoading(false);
            }
          }}
          disabled={isAddDisabled()}
          loading={addLoading}
        />
      </div>
    );
  };
  return (
    <Dialog
      onHide={() => {
        resetForm();
        setShowDialog(false);
      }}
      visible={showDialog}
      header="Add Note"
      footer={footerComponent}
    >
      <AddNoteContainer>
        <span>Please, add your note here!</span>
        <InputTextarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Notes"
          className="w-full mt-3"
        />
        {orderPaymentNotesEnabled && (
          <>
            <div className="flex align-items-center mt-3">
              <Checkbox
                inputId="isPaymentNote"
                checked={paymentData.isPaymentNote}
                onChange={(e) =>
                  setPaymentData({
                    ...paymentData,
                    isPaymentNote: e.checked ?? false,
                    paymentAmount: e.checked ? paymentData.paymentAmount : null,
                    paymentType: e.checked ? paymentData.paymentType : null,
                  })
                }
              />
              <span className="ml-2 cursor-pointer">
                This is a payment note.
              </span>
            </div>
            {paymentData.isPaymentNote && (
              <PaymentFieldsContainer>
                <div className="flex flex-column gap-2">
                  <label htmlFor="paymentAmount">Payment Amount</label>
                  <InputNumber
                    inputId="paymentAmount"
                    value={paymentData.paymentAmount}
                    onValueChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        paymentAmount: e.value ?? null,
                      })
                    }
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    placeholder="Enter payment amount"
                    className="w-full"
                  />
                </div>
                <div className="flex flex-column gap-2">
                  <label htmlFor="paymentType">Payment Type</label>
                  <Dropdown
                    inputId="paymentType"
                    value={paymentData.paymentType}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        paymentType: e.value,
                      })
                    }
                    options={paymentTypeOptions}
                    placeholder="Select payment type"
                    className="w-full"
                  />
                </div>
                {showReductionBlock && reducedProviderAmount !== null && (
                  <div className="flex flex-column gap-2">
                    {isPartiallyPaid ? (
                      <span>
                        The amount remaining to pay to {primaryProviderName}{" "}
                        should be reduced by {parsePrice(reducedProviderAmount)}
                        .
                      </span>
                    ) : (
                      <span>
                        {primaryProviderName} patient portion should be reduced
                        by {parsePrice(reducedProviderAmount)}.
                      </span>
                    )}
                    {hasInsufficientPortion && (
                      <small className="p-error">
                        {primaryProviderName} does not have enough patient
                        portion ({parsePrice(primaryProviderPortion)}) to be
                        reduced by {parsePrice(reducedProviderAmount)}.
                      </small>
                    )}
                    {blockedByTransfer && (
                      <small className="text-primary-400">
                        Transfer is being processed
                      </small>
                    )}
                    {blockedByInstallment && (
                      <small className="p-error">
                        Remaining amount after reduction is not enough to pay
                        monthly installment amount. Please, make changes to
                        payment plan.
                      </small>
                    )}
                    {blockedByRemaining && (
                      <small className="p-error">
                        {primaryProviderName} only has{" "}
                        {primaryProviderRemainingDisplay} remaining to pay,
                        which is not enough to be reduced by{" "}
                        {parsePrice(reducedProviderAmount)}.
                      </small>
                    )}
                    <div className="flex align-items-center">
                      <Checkbox
                        inputId="autoReduce"
                        checked={autoReduce}
                        onChange={(e) => setAutoReduce(e.checked ?? false)}
                        disabled={autoReduceBlocked}
                      />
                      <label
                        htmlFor="autoReduce"
                        className="ml-2 cursor-pointer"
                      >
                        Auto reduce
                      </label>
                    </div>
                  </div>
                )}
              </PaymentFieldsContainer>
            )}
          </>
        )}
      </AddNoteContainer>
    </Dialog>
  );
}
